import { capitalize, isEmpty } from 'lodash';
import * as z from 'zod';

import { StrictAddress } from '../../Address';
import { LosId } from '../../BrandedIds';
import { OriginationData } from '../../loan/OriginationData';
import { OptionalPhoneNumber } from '../../Phone';
import { typedSafeParse } from '../../utils';
import { CamelizeEnum } from '../../zod';
import { OptionalEmailString } from '../email';
import {
  AddressLine,
  Checkbox,
  CountryCode,
  DateString,
  Float,
  MonetaryValue,
  NonNegativeFloat,
  NonNegativeInteger,
  NonNegativeMonetaryValue,
  NonNegativePercentage,
  SocialSecurityNumber,
  StateAbbreviation,
  Zipcode,
} from '../fields';
import { unsafeCreateCastedSchema } from '../utils';
import { LoanPurposeInput, OccupancyTypeInput } from './CreateLoanRow';
import { InvestorSplit } from './RecordDrawDisbursementRow';
import { AddressFloodFields, TARGET_AREA_PROPERTY_DESCRIPTION } from './sharedFields/AddressFields';
import { UpdateLoanFeeOverrideFields } from './sharedFields/LoanFeeOverrides';
import { MERSCreateFields } from './sharedFields/MERSImportFields';

const ProductType = z.enum(['conv', 'va', 'fha', 'usda', 'pih', 'bridge', 'businessPurpose', 'jumbo']);

export type PrimaryBorrowerAddressSchema = z.infer<typeof PrimaryBorrowerAddressSchema>;
export const hasPrimaryBorrowerAddressFields = (data: PrimaryBorrowerAddressSchema) =>
  [
    data.primaryBorrowerMailingAddressLine1,
    data.primaryBorrowerMailingAddressLine2,
    data.primaryBorrowerMailingAddressLine3,
    data.primaryBorrowerMailingAddressLine4,
    data.primaryBorrowerMailingAddressLocality,
    data.primaryBorrowerMailingAddressPostcode,
    data.primaryBorrowerMailingAddressRegion,
  ].some((value) => !isEmpty(value));

const PrimaryBorrowerAddressSchema = z
  .object({
    primaryBorrowerMailingAddressLine1: AddressLine.optional(),
    primaryBorrowerMailingAddressLine2: AddressLine.optional(),
    primaryBorrowerMailingAddressLine3: AddressLine.optional(),
    primaryBorrowerMailingAddressLine4: AddressLine.optional(),
    primaryBorrowerMailingAddressLocality: AddressLine.optional(),
    primaryBorrowerMailingAddressRegion: z.string().optional().or(z.literal('')).meta({
      description: 'If primaryBorrowerMailingAddressCountry is US, this should be a two letter state abbreviation.',
      example: 'CA',
    }),
    // Custom refinements with strings that could be blank are funky,
    // the author of Zod had recommendation to use `.or(z.literal(''))`
    // https://github.com/colinhacks/zod/issues/310#issuecomment-794533682
    primaryBorrowerMailingAddressPostcode: z.string().optional().or(z.literal('')).meta({
      description: 'If country is US, this should be a 5 or 9 digit zip code.',
      example: '90210',
    }),
    primaryBorrowerMailingAddressCountry: CountryCode.optional().or(z.literal('')),
  })
  // Typically refinements only run when the base schema passes validation,
  // so if we want these refinements to run in cases where other parts of
  // the base schema validation might fail it's suggested to split up your
  // schema and run these refinements on a subset of items so they are likely
  // to run AND we avoid the problem of having to cast to `any` and loose all
  // type safety
  .superRefine((data, ctx) => {
    // If any mailing address field is populated, then require
    // full valid address
    const hasAddressField = hasPrimaryBorrowerAddressFields(data);

    // No address information was provided, exit validation early
    if (!hasAddressField) return;

    const address = typedSafeParse(StrictAddress, {
      line1: data.primaryBorrowerMailingAddressLine1 ?? '',
      line2: data.primaryBorrowerMailingAddressLine2,
      line3: data.primaryBorrowerMailingAddressLine3,
      line4: data.primaryBorrowerMailingAddressLine4,
      locality: data.primaryBorrowerMailingAddressLocality ?? '',
      region: data.primaryBorrowerMailingAddressRegion ?? '',
      postcode: data.primaryBorrowerMailingAddressPostcode ?? '',
      country: data.primaryBorrowerMailingAddressCountry ?? 'USA',
    });

    // Has valid address, exit validation early
    if (address.success) return;

    const errorTree = z.treeifyError(address.error);
    for (const [key, issue] of Object.entries(errorTree.properties ?? {})) {
      ctx.addIssue({
        code: 'custom',
        message: issue.errors.join(', '),
        path: [`primaryBorrowerMailingAddress${capitalize(key)}`], // This maps keys to the correct field key
      });
    }

    ctx.addIssue({
      code: 'custom',
      path: ['primaryBorrowerMailingAddressLine1'],
      message: 'Missing full primary borrower mailing address',
    });
  });

const LoanStatusSchema = z
  .object({
    loanStatus: z.literal('active').optional(),
    firstPaymentCollectedDate: DateString.optional().meta({
      description: 'The date of the first payment to be collected in Willow',
    }),
    incomingTransferEffectiveDate: DateString.optional(),
    principalPurchased: NonNegativeFloat.optional(),
    servicerPurchaseDate: DateString.optional(),
    servicerPurchasedEscrowBalance: Float.optional(),
    servicerPurchasedReserveBalance: NonNegativeFloat.optional(),
    initialReserveBalance: NonNegativeFloat.optional(),
    previousLoanOwner: z.string().optional(),
    correspondentLender: z.string().optional(),
    correspondentLenderPhone: OptionalPhoneNumber,
    correspondentLenderEmail: OptionalEmailString,
  })
  .superRefine((data, ctx) => {
    if (data.loanStatus === 'active' && data.firstPaymentCollectedDate != null) {
      const missingFields: string[] = [];
      if (data.principalPurchased == null) missingFields.push('principalPurchased');
      if (data.servicerPurchaseDate == null) missingFields.push('servicerPurchaseDate');
      if (missingFields.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['loanStatus'],
          message: `Missing required fields to set loan status to active: ${missingFields.join(', ')}`,
        });
      }
    }
  });

const EntitySchema = z.object({
  entityCompanyId: z.string().optional().describe('Required when entityName is present.'),
  entityName: z.string().optional().describe('Required when entityCompanyId is present.'),
  entityEin: z.string().optional().describe('Required when entityName or entityCompanyId is present.'),
  entityAddressLine1: AddressLine.optional(),
  entityAddressLine2: AddressLine.optional(),
  entityAddressLocality: AddressLine.optional(),
  entityAddressRegion: StateAbbreviation.optional().or(z.literal('')),
  entityAddressPostcode: Zipcode.optional().or(z.literal('')),
  entityAddressCountry: z.literal('USA').optional(),
  entityPhone: OptionalPhoneNumber,
  entityEmail: OptionalEmailString,
});

const PropertySchema = z.object({
  addressCoverageAmount: NonNegativeMonetaryValue.optional(),
  addressReplacementCostValue: NonNegativeMonetaryValue.optional(),
  addressPropertyType: z.string().optional(),
  addressPropertySubType: z.string().optional(),
  addressFloodZone: z.string().optional(), // legacy field
  addressFloodInformation: AddressFloodFields.optional(),
  addressPurchasePrice: NonNegativeMonetaryValue.optional(),
  addressNumberOfUnits: NonNegativeInteger.optional(),
  addressTargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  addressPropertyValuationMethodType: z.string().optional(),

  address2CoverageAmount: NonNegativeMonetaryValue.optional(),
  address2ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address2PropertyType: z.string().optional(),
  address2PropertySubType: z.string().optional(),
  address2FloodZone: z.string().optional(),
  address2FloodInformation: AddressFloodFields.optional(),
  address2PurchasePrice: NonNegativeMonetaryValue.optional(),
  address2NumberOfUnits: NonNegativeInteger.optional(),
  address2TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address2PropertyValuationMethodType: z.string().optional(),

  address3CoverageAmount: NonNegativeMonetaryValue.optional(),
  address3ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address3PropertyType: z.string().optional(),
  address3PropertySubType: z.string().optional(),
  address3FloodZone: z.string().optional(),
  address3FloodInformation: AddressFloodFields.optional(),
  address3PurchasePrice: NonNegativeMonetaryValue.optional(),
  address3NumberOfUnits: NonNegativeInteger.optional(),
  address3TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address3PropertyValuationMethodType: z.string().optional(),

  address4CoverageAmount: NonNegativeMonetaryValue.optional(),
  address4ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address4PropertyType: z.string().optional(),
  address4PropertySubType: z.string().optional(),
  address4FloodZone: z.string().optional(),
  address4FloodInformation: AddressFloodFields.optional(),
  address4PurchasePrice: NonNegativeMonetaryValue.optional(),
  address4NumberOfUnits: NonNegativeInteger.optional(),
  address4TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address4PropertyValuationMethodType: z.string().optional(),

  address5CoverageAmount: NonNegativeMonetaryValue.optional(),
  address5ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address5PropertyType: z.string().optional(),
  address5PropertySubType: z.string().optional(),
  address5FloodZone: z.string().optional(),
  address5FloodInformation: AddressFloodFields.optional(),
  address5PurchasePrice: NonNegativeMonetaryValue.optional(),
  address5NumberOfUnits: NonNegativeInteger.optional(),
  address5TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address5PropertyValuationMethodType: z.string().optional(),

  address6CoverageAmount: NonNegativeMonetaryValue.optional(),
  address6ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address6PropertyType: z.string().optional(),
  address6PropertySubType: z.string().optional(),
  address6FloodZone: z.string().optional(),
  address6FloodInformation: AddressFloodFields.optional(),
  address6PurchasePrice: NonNegativeMonetaryValue.optional(),
  address6NumberOfUnits: NonNegativeInteger.optional(),
  address6TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address6PropertyValuationMethodType: z.string().optional(),

  address7CoverageAmount: NonNegativeMonetaryValue.optional(),
  address7ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address7PropertyType: z.string().optional(),
  address7PropertySubType: z.string().optional(),
  address7FloodZone: z.string().optional(),
  address7FloodInformation: AddressFloodFields.optional(),
  address7PurchasePrice: NonNegativeMonetaryValue.optional(),
  address7NumberOfUnits: NonNegativeInteger.optional(),
  address7TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address7PropertyValuationMethodType: z.string().optional(),

  address8CoverageAmount: NonNegativeMonetaryValue.optional(),
  address8ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address8PropertyType: z.string().optional(),
  address8PropertySubType: z.string().optional(),
  address8FloodZone: z.string().optional(),
  address8FloodInformation: AddressFloodFields.optional(),
  address8PurchasePrice: NonNegativeMonetaryValue.optional(),
  address8NumberOfUnits: NonNegativeInteger.optional(),
  address8TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),

  address9CoverageAmount: NonNegativeMonetaryValue.optional(),
  address9ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address9PropertyType: z.string().optional(),
  address9PropertySubType: z.string().optional(),
  address9FloodZone: z.string().optional(),
  address9FloodInformation: AddressFloodFields.optional(),
  address9PurchasePrice: NonNegativeMonetaryValue.optional(),
  address9NumberOfUnits: NonNegativeInteger.optional(),
  address9TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address9PropertyValuationMethodType: z.string().optional(),

  address10CoverageAmount: NonNegativeMonetaryValue.optional(),
  address10ReplacementCostValue: NonNegativeMonetaryValue.optional(),
  address10PropertyType: z.string().optional(),
  address10PropertySubType: z.string().optional(),
  address10FloodZone: z.string().optional(),
  address10FloodInformation: AddressFloodFields.optional(),
  address10PurchasePrice: NonNegativeMonetaryValue.optional(),
  address10NumberOfUnits: NonNegativeInteger.optional(),
  address10TargetAreaProperty: Checkbox.optional().describe(TARGET_AREA_PROPERTY_DESCRIPTION),
  address10PropertyValuationMethodType: z.string().optional(),
});

const FeeOverridesSchema = UpdateLoanFeeOverrideFields;

// Conditional refinement approach mentioned here
// https://github.com/colinhacks/zod/issues/479#issuecomment-1536233005
const UpdateLoanRowFields = z.object({
  loanId: LosId,
  primaryBorrowerSsn: SocialSecurityNumber.optional(),
  productType: CamelizeEnum(ProductType).optional(),
  loanPurpose: CamelizeEnum(LoanPurposeInput).optional(),
  maxLineOfCredit: NonNegativeMonetaryValue.optional(),
  occupancyType: CamelizeEnum(OccupancyTypeInput).optional(),
  mortgageInsuranceAmount: NonNegativeMonetaryValue.optional(),
  initialMortgageInsuranceAmount: NonNegativeMonetaryValue.optional(),
  initialDiscountPointsAmount: MonetaryValue.optional(),
  loanLegalDescription: z.string().optional(),
  numberOfProperties: NonNegativeInteger.optional().describe(
    'Number of properties securing the mortgage. Used for 1098 tax forms',
  ),
  currentOutstandingBalance: NonNegativeFloat.optional().describe(
    'Use this field if you are changing the first firstPaymentCollectedDate on the loan. If any payments were already collected by a previous servicer, Willow must know the current outstanding principal balance of the loan.',
  ),
  originalLoanPrincipal: NonNegativeMonetaryValue.optional().describe(
    'Starting principal balance. This value should exclude principal from initial draws provided with the request. Note: we can only process this change if the loan is in the first month on the Willow platform or if it is Dutch.',
  ),
  beginningOfYearPrincipalBalance: NonNegativeMonetaryValue.optional().describe(
    'A loan that has been active since the beginning of the year should include this number for tax reporting. If it’s missing, the workflow to create a Form 1098 will flag it for review.',
  ),
  isAcquired: Checkbox.optional(),
  acquisitionDate: DateString.optional(),
  escrowBalance: Float.optional(),
  interestCollected: Float.optional(),
  ytdInterestPaid: NonNegativeMonetaryValue.optional(),
  principalPrepayment: NonNegativeFloat.optional(),
  suspenseBalance: NonNegativeFloat.optional(),
  initialNonInterestBearingUnpaidPrincipal: NonNegativeMonetaryValue.optional().describe(
    'Deferred principal that is non-interest bearing prior to boarding and should be paid off.',
  ),
  create1098Form: Checkbox.optional().meta({
    description: 'Default: true. Set to false to prevent 1098 from generating at year end',
    default: true,
  }),
  includeInCreditReport: Checkbox.optional().meta({
    description: 'Default: true. Set to false to exclude loan from Metro2® credit report',
    default: true,
  }),
  defaultInterestRateDecimal: NonNegativeFloat.optional(),
  defaultInterestRatePercent: NonNegativePercentage.optional(),
  defaultInterestRemittanceSplit: InvestorSplit.optional().describe(
    'If not provided, default interest will be remitted 100% to the servicer',
  ),

  originationData: OriginationData.optional(),

  // stop settings
  stopEmails: Checkbox.optional().meta({
    description: 'If true, emails will not be sent to the borrowers on the loan',
    example: true,
  }),
  stopPayments: Checkbox.optional().meta({
    description: 'If true, payments will not be accepted for the loan',
    example: true,
  }),
  exactPaymentsOnly: Checkbox.optional(),
  omitFromDefaultInterestAutomation: Checkbox.optional().meta({
    description: 'If true, default interest will not be automatically applied to the loan.',
    example: true,
  }),
  omitFromLateFeeAutomation: Checkbox.optional().meta({
    description:
      'If true, late fees will not be automatically applied to the loan. Note: defaults to true for loans with status bankruptcy, liquidation, or reo.',
    example: true,
    default: false,
  }),
  omitFromInterestOnEscrow: Checkbox.optional().meta({
    description: 'If true, the loan will be omitted from the interest on escrow report.',
    example: true,
    default: false,
  }),
  miDisbursementsPaused: Checkbox.optional().meta({
    description: 'If true, mortgage insurance disbursements will be paused for the loan.',
    example: true,
    default: false,
  }),

  mers: MERSCreateFields.optional(),

  // Renovo fields
  originalNoteHolder: z.string().optional(),
  servicerOrgId: z.string().optional(),
  subServicerOrgId: z.string().optional(),
  investorOrgId: z.string().optional(),
  ppc1OrgId: z.string().optional(),
});

export const UpdateLoanRow = UpdateLoanRowFields.and(PrimaryBorrowerAddressSchema)
  .and(LoanStatusSchema)
  .and(EntitySchema)
  .and(PropertySchema)
  .and(FeeOverridesSchema);

export type UpdateLoanRow = z.infer<typeof UpdateLoanRow>;

// The REST API infers `loanId` from the URL parameter, so the public docs advertise it
// as optional. ZodIntersection has no .extend, and the component schemas above are
// file-local, so the docs variant is composed here. Takes the loanId schema as a
// parameter so the docs package can attach its own description/deprecation metadata.
export const UpdateLoanRowForDocs = (loanIdSchema: z.ZodType) =>
  UpdateLoanRowFields.extend({ loanId: loanIdSchema })
    .and(PrimaryBorrowerAddressSchema)
    .and(LoanStatusSchema)
    .and(EntitySchema)
    .and(PropertySchema)
    .and(FeeOverridesSchema);

export const UpdateLoanRowRefinements = unsafeCreateCastedSchema(UpdateLoanRow);
